// Conservative legacy support: only explicitly labelled, rectangular arrays.
// Never infer a product, complete a ragged row, or reinterpret prose/bullets.
const symbols = '●•○◯■□▲△★☆';
const rowPattern = new RegExp(`^[\\t \u00a0]*([${symbols}](?:[\\t \u00a0]*[${symbols}])*)[\\t \u00a0]*$`, 'u');
const escape = value => String(value).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

export function findCounterArrays(value) {
  const text = String(value ?? '');
  if (text.length > 100000) return [];
  const lines = [...text.matchAll(/[^\n]*(?:\n|$)/g)].filter(m => m[0]);
  const found = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i][0].replace(/\r?\n$/, '');
    const label = line.match(/^[\t \u00a0]*((?:Dot |Counter )?Array)[\t \u00a0]*:[\t \u00a0]*(.*)$/i);
    if (!label) continue;
    const rows = [];
    let end = lines[i].index + line.length;
    let invalid = false;
    const add = row => {
      const match = row.match(rowPattern);
      if (!match) return false;
      const marks = [...match[1]].filter(c => symbols.includes(c));
      rows.push(marks);
      return true;
    };
    if (label[2] && !add(label[2])) continue;
    let j = i + 1;
    for (; j < lines.length; j++) {
      const next = lines[j][0].replace(/\r?\n$/, '');
      // Legacy passages sometimes place a blank line between array rows.
      if (!next.trim() && j + 1 < lines.length && rowPattern.test(lines[j + 1][0].trim())) continue;
      if (!add(next)) break;
      end = lines[j].index + next.length;
    }
    const cols = rows[0]?.length || 0;
    invalid = rows.length < 2 || rows.length > 20 || cols < 2 || cols > 20
      || rows.some(row => row.length !== cols || row.some(c => c !== rows[0][0]));
    if (!invalid) found.push({ start: lines[i].index, end, rows: rows.length, columns: cols, symbol: rows[0][0], label: label[1], source: text.slice(lines[i].index, end) });
    i = j - 1;
  }
  return found;
}

export function counterArrayHtml(model) {
  const description = `${model.label}: ${model.rows} rows with ${model.columns} counters in each row.`;
  const dot = ['●', '•'].includes(model.symbol);
  const cells = Array.from({ length: model.rows * model.columns }, () => `<span aria-hidden="true" style="${dot ? 'display:block;width:.58em;height:.58em;border-radius:50%;background:#176b65;border:1px solid #104e49;box-sizing:border-box;' : 'display:flex;align-items:center;justify-content:center;width:.8em;height:.8em;line-height:1;color:#176b65;'}">${dot ? '' : escape(model.symbol)}</span>`).join('');
  // The inner grid never wraps a row. Narrow views scroll within the model.
  return `<span class="assessnow-counter-model" role="img" aria-label="${escape(description)}" style="display:block;max-width:100%;box-sizing:border-box;margin:.65em 0;padding:clamp(12px,2vw,22px);border:1px solid #b6d3cc;border-radius:14px;background:#f3faf7;white-space:normal;overflow-x:auto;break-inside:avoid"><span aria-hidden="true" style="display:block;font-size:.72em;font-weight:700;letter-spacing:.06em;color:#355e57;margin-bottom:1em">${escape(model.label)}</span><span aria-hidden="true" style="display:grid;grid-template-columns:repeat(${model.columns},.8em);gap:.65em .75em;width:max-content;max-width:none;margin:0 auto;padding:.25em;align-items:center;justify-items:center">${cells}</span></span>`;
}

export function renderCounterText(value) {
  const text = String(value ?? '');
  const models = findCounterArrays(text);
  let cursor = 0;
  const parts = [];
  for (const model of models) {
    parts.push(escape(text.slice(cursor, model.start)).replace(/\r?\n/g, '<br>'), counterArrayHtml(model));
    cursor = model.end;
  }
  parts.push(escape(text.slice(cursor)).replace(/\r?\n/g, '<br>'));
  return parts.join('');
}

export function counterTextToSpeech(value) {
  let text = String(value ?? '');
  for (const model of findCounterArrays(text).reverse()) {
    text = text.slice(0, model.start) + `${model.label}: ${model.rows} rows with ${model.columns} counters in each row.` + text.slice(model.end);
  }
  return text;
}
